import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Anchors a search/results dropdown to the space BELOW a modal box, rather
 * than layering it (absolutely, inside the modal's own flow) on top of the
 * modal's own content (Share options #363).
 *
 * Root cause this fixes: `SharedSpaceDialog` / `NoteShareDialog` rendered
 * their contact-picker results as `absolute bottom-full` INSIDE the invite
 * footer, which sits at the bottom of the modal — so a non-trivial results
 * list grew UPWARD and painted directly over the member list / labels above
 * it, mixing illegibly with the modal's own text (exactly the PO's report).
 * The fix keeps the dropdown's visual style untouched and only moves WHERE
 * it renders: portaled to `document.body` (so no clipping ancestor can slice
 * it — the #225/#249 pattern) and positioned `fixed`, anchored horizontally
 * to the search input but vertically BELOW the whole modal box, so it can
 * never overlap any part of the dialog.
 *
 * `open` gates the position effect (no work while the dropdown is hidden).
 * Returns `{ modalRef, anchorRef, rect }` — attach `modalRef` to the visible
 * modal box (NOT the backdrop) and `anchorRef` to the search input's wrapper;
 * `rect` is `null` until measured, then `{ top, left, width, maxHeight }` in
 * viewport (fixed-position) coordinates.
 *
 * Re-anchors (never closes) on scroll/resize — the #249 lesson: a scroll
 * inside a capped-height overlay must reposition it, not dismiss it.
 */
export function useDropdownBelowModal(open) {
    const modalRef = useRef(null);
    const anchorRef = useRef(null);
    const [rect, setRect] = useState(null);

    const recompute = useCallback(() => {
        if (!modalRef.current || !anchorRef.current) return;
        const modalRect = modalRef.current.getBoundingClientRect();
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const gap = 8;
        const edge = 8;
        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;

        let left = anchorRect.left;
        const width = anchorRect.width;
        if (left + width > viewportW - edge) left = Math.max(edge, viewportW - edge - width);
        if (left < edge) left = edge;

        let top = modalRect.bottom + gap;
        // Degenerate case: the modal already reaches the bottom of the
        // viewport (a very short window). Keep the dropdown on-screen with a
        // small floor rather than pushing it off entirely.
        if (top > viewportH - 40) top = viewportH - 40;
        const maxHeight = Math.max(80, viewportH - top - edge);

        setRect({ top, left, width, maxHeight });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        recompute();
        window.addEventListener('resize', recompute);
        // `scroll` doesn't bubble, so a capture-phase window listener is the
        // only way to observe it from any scrollable ancestor (or the
        // dropdown's own internal overflow) — re-anchor, never close.
        window.addEventListener('scroll', recompute, true);
        return () => {
            window.removeEventListener('resize', recompute);
            window.removeEventListener('scroll', recompute, true);
        };
    }, [open, recompute]);

    return { modalRef, anchorRef, rect };
}

export default useDropdownBelowModal;
